import type { ReactElement } from 'react';

import { defaultFormat, describeRange, describeSeries, tableRows, type ValueFormatter } from './alternative';
import { Axes, pickXLabels, plotFrame } from './Axes';
import { ChartFrame } from './ChartFrame';
import { computeDomain, isValue, linearScale, polylinePath, presentRuns, type Maybe } from './geometry';
import { Legend } from './Legend';
import { lineDashAt, seriesTokenAt, useTokens, type SeriesToken } from './seriesPalette';
import { useMeasuredWidth } from './useMeasuredWidth';

/**
 * Courbe multi-series (maquette « Comptes actifs », « Latence de l'API »).
 *
 * Une serie = une courbe, **couleur, motif de trait et rang de legende** : on
 * les distingue sans la couleur. Les valeurs sont posees telles que fournies ;
 * une valeur `null` **coupe la courbe** (la periode manquante n'est ni reliee
 * ni ramenee a zero) et se lit « — » dans la table.
 *
 * Sans aucune valeur sur aucune serie, le graphique n'est pas dessine : une
 * phrase le dit.
 */
export interface LineSeries {
  /** Libelle de la serie, repris par la legende et la table (« Quotidiens »). */
  readonly label: string;
  /** Une valeur par categorie, `null` quand elle manque. */
  readonly values: readonly Maybe[];
  /** Couleur imposee ; sinon celle du rang de la serie. */
  readonly token?: SeriesToken;
  /** Aire legere sous la courbe. */
  readonly area?: boolean;
}

export interface LineChartProps {
  /** Nom accessible : ce que montre le graphique (« Comptes actifs, 90 jours »). */
  readonly label: string;
  /** Abscisse de chaque point (« 5 sept. »), dans l'ordre. */
  readonly categories: readonly string[];
  readonly series: readonly LineSeries[];
  /** Intitule de la premiere colonne de la table (« Jour »). */
  readonly categoryHeader?: string;
  readonly formatValue?: ValueFormatter;
  /** Bornes de l'axe vertical ; la donnee les depasse si elle les depasse. */
  readonly min?: number;
  readonly max?: number;
  readonly height?: number;
  /** Phrase de synthese ecrite par l'appelant ; sinon elle est derivee des valeurs. */
  readonly summary?: string;
  readonly emptyMessage?: string;
  /** Cellule de la table pour une valeur absente, quand « — » en dirait moins (« < 10 »). */
  readonly missingLabel?: string;
}

const FALLBACK_WIDTH = 600;

export const LineChart = ({
  label,
  categories,
  series,
  categoryHeader = 'Date',
  formatValue = defaultFormat,
  min,
  max,
  height = 220,
  summary,
  emptyMessage = 'Aucune donnée sur la période.',
  missingLabel,
}: LineChartProps): ReactElement => {
  const tokens = useTokens();
  const { ref, width } = useMeasuredWidth(FALLBACK_WIDTH);
  const present = series.flatMap((one) => one.values.slice(0, categories.length).filter(isValue));
  const hasData = present.length > 0;

  const domain = computeDomain(hasData ? Math.min(...present) : 0, hasData ? Math.max(...present) : 0, {
    integers: present.every(Number.isInteger),
    ...(min === undefined ? {} : { min }),
    ...(max === undefined ? {} : { max }),
  });
  const frame = plotFrame(width, height, domain, formatValue);
  const xAt = (index: number): number =>
    categories.length <= 1 ? frame.left + frame.plotWidth / 2 : frame.left + (frame.plotWidth * index) / (categories.length - 1);
  const yAt = linearScale(domain.min, domain.max, frame.top + frame.plotHeight, frame.top);

  const resolved = series.map((one, rank) => ({
    ...one,
    token: one.token ?? seriesTokenAt(rank),
    dash: lineDashAt(rank),
  }));

  return (
    <ChartFrame
      ref={ref}
      label={label}
      summary={
        summary ??
        [describeRange(categories), ...series.map((one) => describeSeries(one.label, categories, one.values, formatValue))].join(' ')
      }
      table={{
        categoryHeader,
        columns: series.map((one) => one.label),
        rows: tableRows(categories, series, formatValue, missingLabel),
      }}
      {...(hasData ? {} : { empty: emptyMessage })}
    >
      <svg width={width} height={height} focusable="false" style={{ display: 'block', maxWidth: '100%' }}>
        <Axes frame={frame} domain={domain} yAt={yAt} format={formatValue} xLabels={pickXLabels(frame, categories, xAt)} />
        {resolved.map((one, rank) => {
          const color = tokens[one.token];

          return (
            <g key={rank}>
              {presentRuns(one.values.slice(0, categories.length)).map((run) => {
                const points = run.map((index) => [xAt(index), yAt(one.values[index]!)] as const);
                const first = points[0]!;
                const last = points[points.length - 1]!;
                const baseline = frame.top + frame.plotHeight;

                return run.length === 1 ? (
                  <circle key={run[0]} cx={first[0]} cy={first[1]} r={3} fill={color} />
                ) : (
                  <g key={run[0]}>
                    {one.area === true ? (
                      <path
                        d={`${polylinePath(points)} L ${String(last[0])} ${String(baseline)} L ${String(first[0])} ${String(baseline)} Z`}
                        fill={color}
                        fillOpacity={0.12}
                      />
                    ) : null}
                    <path
                      d={polylinePath(points)}
                      fill="none"
                      stroke={color}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={one.dash}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <Legend items={resolved.map((one) => ({ label: one.label, token: one.token, dash: one.dash, shape: 'line' }))} />
    </ChartFrame>
  );
};
